(() => {
  'use strict';
  if (window.aaWishlistReady) return;
  const configElement = document.getElementById('aa-wishlist-config');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  const key = 'aa:wishlist:v1';
  const root = (window.Shopify?.routes?.root || config.root || '/').replace(/\/?$/, '/');
  const page = document.querySelector('[data-aa-wishlist-page]');
  const grid = page?.querySelector('[data-aa-wishlist-grid]');
  const status = document.querySelector('[data-aa-wishlist-status]');
  const products = new Map();
  const selected = new Set();
  let managing = false;
  let refreshVersion = 0;
  let scheduled = false;
  let storageReadable = true;
  let pendingRemoval = [];

  function read() {
    try {
      const data = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(data)) return [];
      const unique = new Map();
      data.forEach(item => {
        if (item && /^\d+$/.test(String(item.id)) && typeof item.handle === 'string' && /^[^/?#]+$/.test(item.handle)) {
          unique.set(String(item.id), { id: String(item.id), handle: item.handle, title: String(item.title || item.handle), image: String(item.image || '') });
        }
      });
      storageReadable = true;
      return [...unique.values()];
    } catch (_) {
      storageReadable = false;
      return [];
    }
  }

  function announce(message) { if (status) status.textContent = message; }
  function productUrl(handle) { return `${root}products/${encodeURIComponent(handle)}`; }
  function node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function sync() {
    const items = read();
    const ids = new Set(items.map(item => item.id));
    for (const id of selected) if (!ids.has(id)) selected.delete(id);
    document.querySelectorAll('[data-aa-wishlist-count]').forEach(element => {
      if (element.textContent !== String(items.length)) element.textContent = String(items.length);
    });
    document.querySelectorAll('[data-aa-wishlist-toggle]').forEach(button => {
      const saved = ids.has(button.dataset.productId);
      button.hidden = false;
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', `${saved ? config.remove : config.save}: ${button.dataset.productTitle}`);
    });
    updateManagement(items);
    return items;
  }

  function persist(items, message) {
    if (!storageReadable) { announce(config.error); return false; }
    try { localStorage.setItem(key, JSON.stringify(items)); }
    catch (_) { announce(config.error); return false; }
    sync();
    if (message) announce(message);
    renderGrid(items);
    return true;
  }

  function updateManagement(items = read()) {
    if (!page) return;
    if (!items.length) { managing = false; selected.clear(); }
    page.classList.toggle('is-managing', managing);
    const manage = page.querySelector('[data-aa-wishlist-manage]');
    manage.hidden = false;
    manage.disabled = items.length === 0;
    manage.textContent = managing ? config.done : config.manage;
    manage.setAttribute('aria-pressed', String(managing));
    page.querySelector('[data-aa-wishlist-actions]').hidden = !managing;
    page.querySelectorAll('[data-aa-wishlist-select]').forEach(input => {
      input.checked = selected.has(input.dataset.aaWishlistSelect);
      input.closest('label').hidden = !managing;
    });
    const all = page.querySelector('[data-aa-wishlist-all]');
    all.checked = items.length > 0 && selected.size === items.length;
    all.indeterminate = selected.size > 0 && selected.size < items.length;
    page.querySelector('[data-aa-wishlist-delete]').disabled = selected.size === 0;
    page.querySelector('[data-aa-wishlist-buy]').disabled = selected.size === 0;
  }

  function card(item, product) {
    const element = node('article', 'aa-wishlist-card product-index');
    element.dataset.aaWishlistItem = item.id;
    const title = product?.title || item.title;
    const imageBox = node('div', `product--grid-image image_${config.imageRatio || 'natural'}`);
    const link = node('a', 'aa-wishlist-image');
    link.href = productUrl(product?.handle || item.handle);
    link.setAttribute('aria-label', title);
    const ratioBox = node('div', 'box-ratio');
    const aspect = product?.media?.find(media => media.media_type === 'image')?.aspect_ratio || 1;
    const ratio = { square: 1, portrait: 2 / 3, landscape: 4 / 3 }[config.imageRatio] || aspect;
    ratioBox.style.paddingBottom = `${100 / ratio}%`;
    const imageUrl = product?.featured_image || item.image;
    if (imageUrl && /^(https?:)?\/\//.test(imageUrl)) {
      const image = node('img');
      // Position badges within the painted image, preserving the existing contain frame.
      image.addEventListener('load', () => {
        const actualAspect = image.naturalWidth / image.naturalHeight;
        if (!Number.isFinite(actualAspect) || actualAspect <= 0) return;
        imageBox.style.setProperty('--aa-sale-inset-y', `${Math.max(0, (1 - ratio / actualAspect) / 2) * 100}%`);
        imageBox.style.setProperty('--aa-sale-inset-x', `${Math.max(0, (1 - actualAspect / ratio) / 2) * 100}%`);
      }, { once: true });
      image.src = imageUrl;
      image.alt = title;
      image.loading = 'lazy';
      image.width = 700;
      image.height = Math.round(700 / aspect);
      ratioBox.append(image);
    }
    link.append(ratioBox);
    imageBox.append(link);
    if (product && product.compare_at_price > product.price) {
      const sale = node('div', 'sale-item sale-item--icon icn');
      sale.append(node('span', 'icn__inner', config.sale));
      imageBox.append(sale);
    }
    const selection = node('label', 'aa-wishlist-selection');
    selection.hidden = !managing;
    const check = node('input');
    check.type = 'checkbox';
    check.dataset.aaWishlistSelect = item.id;
    check.checked = selected.has(item.id);
    check.setAttribute('aria-label', `${config.select}: ${title}`);
    selection.append(check);
    imageBox.append(selection);
    element.append(imageBox);
    const details = node('div', 'product--details');
    const titleLink = node('a', 'js-product--details-link');
    titleLink.href = link.href;
    titleLink.append(node('h3', 'mb0', title));
    details.append(titleLink);
    if (product && typeof product.price === 'number') {
      const currency = window.Shopify?.currency?.active || config.currency;
      details.append(node('p', 'price price--listing', new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(product.price / 100)));
    }
    element.append(details);
    return element;
  }

  function renderGrid(items = read()) {
    if (!page) return;
    const empty = page.querySelector('[data-aa-wishlist-empty]');
    empty.hidden = items.length > 0;
    grid.replaceChildren(...items.map(item => card(item, products.get(item.id))));
    updateManagement(items);
  }

  async function inspect(item) {
    try {
      const response = await fetch(`${productUrl(item.handle)}.js`, { cache: 'no-store', credentials: 'same-origin' });
      if (response.status === 404 || response.status === 410) {
        if (!response.headers.get('content-type')?.includes('json')) return { item };
        const confirmation = await fetch(`${productUrl(item.handle)}.json`, { cache: 'no-store', credentials: 'same-origin' });
        return { item, gone: (confirmation.status === 404 || confirmation.status === 410) && !!confirmation.headers.get('content-type')?.includes('json') };
      }
      if (!response.ok) return { item };
      const product = await response.json();
      if (!product || !product.id || !Array.isArray(product.variants)) return { item };
      if (String(product.id) !== item.id || !product.variants.length) return { item, gone: true };
      return { item, product };
    } catch (_) { return { item }; }
  }

  async function refresh() {
    const version = ++refreshVersion;
    const items = sync();
    if (page) {
      page.querySelector('[data-aa-wishlist-loading]').hidden = !items.length || grid.children.length > 0;
      grid.setAttribute('aria-busy', String(items.length > 0));
    }
    const results = await Promise.all(items.map(inspect));
    if (version !== refreshVersion) return;
    const gone = new Set();
    results.forEach(result => {
      if (result.gone) gone.add(result.item.id);
      else if (result.product) products.set(result.item.id, result.product);
    });
    const current = read();
    const remaining = current.filter(item => !gone.has(item.id) || !items.some(original => original.id === item.id && original.handle === item.handle));
    if (remaining.length !== current.length) persist(remaining);
    sync();
    renderGrid(read());
    if (page) {
      page.querySelector('[data-aa-wishlist-loading]').hidden = true;
      grid.setAttribute('aria-busy', 'false');
    }
  }

  function configureSelected() {
    const dialog = page.querySelector('[data-aa-wishlist-configure]');
    const list = dialog.querySelector('[data-aa-wishlist-configure-list]');
    list.replaceChildren();
    read().filter(item => selected.has(item.id)).forEach(item => {
      const product = products.get(item.id);
      const row = node('li');
      row.append(node('p', '', product?.title || item.title));
      if (product?.available === false) row.append(node('p', '', config.soldOut));
      else {
        const link = node('a', 'button', config.configure);
        link.href = productUrl(product?.handle || item.handle);
        link.target = '_blank';
        link.rel = 'noopener';
        row.append(link);
      }
      list.append(row);
    });
    dialog.showModal();
  }

  document.addEventListener('click', event => {
    const toggle = event.target.closest('[data-aa-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const items = read();
      const id = toggle.dataset.productId;
      if (items.some(item => item.id === id)) persist(items.filter(item => item.id !== id), config.removed);
      else persist([...items, { id, handle: toggle.dataset.productHandle, title: toggle.dataset.productTitle, image: toggle.dataset.productImage }], config.saved);
      refresh();
      return;
    }
    if (!page) return;
    if (event.target.closest('[data-aa-wishlist-manage]')) {
      managing = !managing;
      if (!managing) selected.clear();
      updateManagement();
    } else if (event.target.closest('[data-aa-wishlist-delete]')) {
      pendingRemoval = [...selected];
      page.querySelector('[data-aa-wishlist-confirm]').showModal();
    } else if (event.target.closest('[data-aa-wishlist-confirm-remove]')) {
      const ids = new Set(pendingRemoval);
      page.querySelector('[data-aa-wishlist-confirm]').close();
      persist(read().filter(item => !ids.has(item.id)), config.removed);
    } else if (event.target.closest('[data-aa-wishlist-buy]')) configureSelected();
    else if (event.target.closest('[data-aa-wishlist-close]')) event.target.closest('dialog').close();
    else if (managing && event.target.closest('[data-aa-wishlist-item]') && !event.target.closest('input,label')) {
      event.preventDefault();
      const id = event.target.closest('[data-aa-wishlist-item]').dataset.aaWishlistItem;
      if (selected.has(id)) selected.delete(id); else selected.add(id);
      updateManagement();
    }
  }, true);

  document.addEventListener('change', event => {
    if (event.target.matches('[data-aa-wishlist-select]')) {
      const id = event.target.dataset.aaWishlistSelect;
      if (event.target.checked) selected.add(id); else selected.delete(id);
      updateManagement();
    } else if (event.target.matches('[data-aa-wishlist-all]')) {
      selected.clear();
      if (event.target.checked) read().forEach(item => selected.add(item.id));
      updateManagement();
    }
  });

  new MutationObserver(records => {
    if (scheduled || !records.some(record => [...record.addedNodes].some(n => n.nodeType === 1 && (n.matches?.('[data-aa-wishlist-toggle], [data-aa-wishlist-count]') || n.querySelector?.('[data-aa-wishlist-toggle], [data-aa-wishlist-count]'))))) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; sync(); });
  }).observe(document.body, { childList: true, subtree: true });
  window.addEventListener('storage', event => { if (event.key === key || event.key === null) { sync(); refresh(); } });
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  window.aaWishlistReady = true;
  sync();
  refresh();
})();
